import type {
  OnboardingStatus,
  OnboardingTask,
  OnboardingTaskStatus,
} from 'src/entities/OnboardingStatus';

import { STUDIO_CHECKLIST_TASK_CODE_BY_ID } from './studioChecklist';

/**
 * Checklist 1 of the onboarding-activation Getting started card, client
 * activation first. Order here is the order the rows render in.
 */
export const CHECKLIST_ONE_STEP_IDS = ['explorePortal', 'publishApp'] as const;

/**
 * Checklist 1 for a prompt or template signup, which arrives mid-way through
 * building its first app, so that step leads.
 */
export const APP_FIRST_CHECKLIST_ONE_STEP_IDS = [
  'publishApp',
  'explorePortal',
] as const satisfies readonly ChecklistOneStepId[];

/** Checklist 2, "Set up your workspace". Its steps can be done in any order. */
export const CHECKLIST_TWO_STEP_IDS = [
  'branding',
  'anotherApp',
  'contacts',
  'personalizedGuidance',
] as const;

export type ChecklistOneStepId = (typeof CHECKLIST_ONE_STEP_IDS)[number];
export type ChecklistTwoStepId = (typeof CHECKLIST_TWO_STEP_IDS)[number];
export type ActivationStepId = ChecklistOneStepId | ChecklistTwoStepId;

export type ActivationStepCompletion = Record<ActivationStepId, boolean>;

/**
 * Which part of the card shows: checklist 1, the "You're on a roll" card that
 * unlocks checklist 2, or checklist 2.
 */
export type ActivationStage = 'checklistOne' | 'unlock' | 'checklistTwo';

/** Values for the guided-tour param that open checklist 2's guided modals. */
export const GUIDED_TOUR_ANOTHER_APP = 'anotherApp';
export const GUIDED_TOUR_FIRST_CONTACT = 'firstContact';

// Plain strings: the checklist 2 codes are not in the classic TaskCodes
// enum, which the classic onboarding card switches over exhaustively.
const ACTIVATION_TASK_CODE_BY_ID: Record<ActivationStepId, string> = {
  explorePortal: STUDIO_CHECKLIST_TASK_CODE_BY_ID.explorePortal,
  publishApp: STUDIO_CHECKLIST_TASK_CODE_BY_ID.publishApp,
  branding: 'customization',
  anotherApp: 'anotherApp',
  contacts: 'firstContact',
  personalizedGuidance: 'personalizedGuidance',
};

// The task shape with a plain string code, so the API's onboarding tasks fit it
// and so do the codes the classic enum leaves out.
export type ActivationTask = Pick<OnboardingTask, 'done' | 'status'> & {
  taskCode: string;
};

// "Do this later" checks a step off just like doing it.
const isTaskComplete = (task: ActivationTask | undefined): boolean =>
  task?.done === true || task?.status === 'done' || task?.status === 'skipped';

/**
 * Whether the response carries the onboarding-activation checklist. The backend
 * only sends checklist 2 to Studio workspaces with the flag on, so a response
 * without it (classic, flag off) keeps today's two-task card.
 */
export const hasActivationTasks = (
  tasks: readonly ActivationTask[] | undefined,
): boolean =>
  (tasks ?? []).some(
    (task) => task.taskCode === ACTIVATION_TASK_CODE_BY_ID.anotherApp,
  );

// The backend only moves a task forward, so an optimistic write that would move one
// back is rejected there and must not show here either.
const TASK_STATUS_RANK: Record<OnboardingTaskStatus, number> = {
  todo: 0,
  'in-progress': 1,
  skipped: 2,
  done: 3,
};

export const canTaskStatusAdvance = (
  from: OnboardingTaskStatus | undefined,
  to: OnboardingTaskStatus,
): boolean => TASK_STATUS_RANK[to] >= TASK_STATUS_RANK[from ?? 'todo'];

export const getActivationStepCompletion = (
  tasks: readonly ActivationTask[] | undefined,
): ActivationStepCompletion => {
  const taskByCode = new Map(
    (tasks ?? []).map((task) => [task.taskCode, task]),
  );
  const isComplete = (id: ActivationStepId) =>
    isTaskComplete(taskByCode.get(ACTIVATION_TASK_CODE_BY_ID[id]));
  // Spelled out so a new step can't silently go unread.
  return {
    explorePortal: isComplete('explorePortal'),
    publishApp: isComplete('publishApp'),
    branding: isComplete('branding'),
    anotherApp: isComplete('anotherApp'),
    contacts: isComplete('contacts'),
    personalizedGuidance: isComplete('personalizedGuidance'),
  };
};

export const countCompletedSteps = (
  stepIds: readonly ActivationStepId[],
  completion: ActivationStepCompletion,
): number => stepIds.filter((id) => completion[id]).length;

export const areStepsCompleted = (
  stepIds: readonly ActivationStepId[],
  completion: ActivationStepCompletion,
): boolean => stepIds.every((id) => completion[id]);

/** Whether both checklists are done, the state that hides the card on next load. */
export const areActivationStepsCompleted = (
  completion: ActivationStepCompletion,
): boolean =>
  areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion) &&
  areStepsCompleted(CHECKLIST_TWO_STEP_IDS, completion);

/**
 * The checklist the owner is working through, as the client preview card counts it:
 * checklist 1 until it's done, then checklist 2. The backend's preview summary
 * counts the same way.
 */
export const getCurrentChecklistSteps = (
  completion: ActivationStepCompletion,
): { completed: number; total: number } => {
  const stepIds = areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion)
    ? CHECKLIST_TWO_STEP_IDS
    : CHECKLIST_ONE_STEP_IDS;
  return {
    completed: countCompletedSteps(stepIds, completion),
    total: stepIds.length,
  };
};

/**
 * The steps the CRM's "Sign in as client" link carries. Signing in as the
 * owner's test client completes the preview step on arrival, after the link is
 * built, so it counts here or the card would open one step behind.
 */
export const getPreviewLinkSteps = (
  completion: ActivationStepCompletion,
  isOwnersTestClient: boolean,
): { completed: number; total: number } =>
  getCurrentChecklistSteps(
    isOwnersTestClient ? { ...completion, explorePortal: true } : completion,
  );

export const getActivationStage = (
  completion: ActivationStepCompletion,
  seenAt: OnboardingStatus['fields']['seenAt'],
): ActivationStage => {
  if (!areStepsCompleted(CHECKLIST_ONE_STEP_IDS, completion)) {
    return 'checklistOne';
  }
  // The card records part2Unlock when it shows, so a reload doesn't bring it back.
  return seenAt?.part2Unlock ? 'checklistTwo' : 'unlock';
};
